import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, LogOut, Loader2, Layout, Trash2, Sliders } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { PRESETS } from "@/components/simulator/SimulatorPresets";
import AdminGroupLayoutEditor from "@/pages/admin/AdminGroupLayoutEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLayoutPresets = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [globalPreset, setGlobalPreset] = useState("full");
  const [groups, setGroups] = useState([]); // all user groups
  const [groupPresets, setGroupPresets] = useState({}); // {group_id -> preset}
  const [saving, setSaving] = useState(false);
  const [editorFor, setEditorFor] = useState(null); // {id, name} for the group being edited

  const load = useCallback(async () => {
    try {
      const [prefRes, groupsRes] = await Promise.all([
        axios.get(`${API}/layout-preference/admin`, { headers: getAuthHeaders() }),
        axios.get(`${API}/user-groups`, { headers: getAuthHeaders() }),
      ]);
      setGlobalPreset(prefRes.data?.global || "full");
      const map = {};
      (prefRes.data?.groups || []).forEach((g) => { map[g.group_id] = g.preset; });
      setGroupPresets(map);
      setGroups(groupsRes.data || []);
    } catch (err) {
      toast.error("Erro ao carregar preferências: " + (err?.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const saveGlobal = async (preset) => {
    setSaving(true);
    try {
      await axios.put(`${API}/layout-preference/admin/global`, { preset }, { headers: getAuthHeaders() });
      setGlobalPreset(preset);
      toast.success(`Preset global atualizado: ${PRESETS[preset].label}`);
    } catch (err) {
      toast.error("Erro ao guardar preset global");
    } finally {
      setSaving(false);
    }
  };

  const saveGroup = async (groupId, preset) => {
    setSaving(true);
    try {
      await axios.put(`${API}/layout-preference/admin/group`, { group_id: groupId, preset }, { headers: getAuthHeaders() });
      setGroupPresets((prev) => ({ ...prev, [groupId]: preset }));
      toast.success(`Preset do grupo atualizado`);
    } catch (err) {
      toast.error("Erro ao guardar preset do grupo");
    } finally {
      setSaving(false);
    }
  };

  const clearGroup = async (groupId) => {
    if (!window.confirm("Remover definição de layout deste grupo? Passará a usar o layout global.")) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/layout-preference/admin/group/${groupId}`, { headers: getAuthHeaders() });
      setGroupPresets((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
      toast.success("Preset removido — grupo passa a usar layout global");
    } catch (err) {
      toast.error("Erro ao remover preset do grupo");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-background font-sans" data-testid="admin-layout-presets-page">
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-[1800px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="w-4 h-4" />Voltar</Button></Link>
            <div className="flex items-center gap-2">
              <Layout className="w-6 h-6 text-brand-600" />
              <h1 className="text-xl font-bold text-brand-700">Layouts do Simulador</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Olá, <span className="font-medium">{user?.name}</span></span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2" />Sair</Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-8 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Como funciona</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-600 space-y-2">
            <p>Cada utilizador vê o layout do simulador segundo esta prioridade:</p>
            <ol className="list-decimal ml-6 space-y-1">
              <li><strong>Preferência pessoal</strong> — se o utilizador escolheu um preset no seletor no topo do simulador</li>
              <li><strong>Preset do grupo</strong> — se aqui definir um preset para o grupo do utilizador</li>
              <li><strong>Preset global</strong> — se não houver override, aplica-se a todos os utilizadores</li>
            </ol>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-brand-600" /></div>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Preset Global</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-3">Este é o layout que se aplica a todos os utilizadores sem definição específica.</p>
                <div className="flex items-center gap-3">
                  <Select value={globalPreset} onValueChange={saveGlobal} disabled={saving}>
                    <SelectTrigger className="w-72" data-testid="global-preset-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.values(PRESETS).map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.label} — {p.desc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {saving && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Preset por Grupo</CardTitle>
              </CardHeader>
              <CardContent>
                {groups.length === 0 ? (
                  <p className="text-sm text-slate-500">Ainda não há grupos criados. <Link to="/admin/users" className="text-brand-600 underline">Criar em Utilizadores → Grupos</Link>.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Grupo</TableHead>
                        <TableHead>Preset atual</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groups.map((g) => {
                        const current = groupPresets[g.id] || "";
                        return (
                          <TableRow key={g.id} data-testid={`group-row-${g.id}`}>
                            <TableCell className="font-medium">{g.name}<div className="text-xs text-slate-500">{g.description || "—"}</div></TableCell>
                            <TableCell>
                              <Select value={current || "__inherit__"} onValueChange={(v) => v === "__inherit__" ? clearGroup(g.id) : saveGroup(g.id, v)}>
                                <SelectTrigger className="w-64" data-testid={`group-preset-select-${g.id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__inherit__">Herdar do global ({PRESETS[globalPreset]?.label})</SelectItem>
                                  {Object.values(PRESETS).map((p) => (
                                    <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="outline" size="sm" onClick={() => setEditorFor({ id: g.id, name: g.name })} className="mr-2" data-testid={`edit-group-layout-${g.id}`}>
                                <Sliders className="w-4 h-4 mr-1" /> Widgets & Layout
                              </Button>
                              {current && (
                                <Button variant="ghost" size="sm" onClick={() => clearGroup(g.id)} data-testid={`clear-group-${g.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>

      {/* Group Layout Editor Modal */}
      <Dialog open={!!editorFor} onOpenChange={(open) => !open && setEditorFor(null)}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Widgets &amp; Layout — Grupo &quot;{editorFor?.name}&quot;</DialogTitle>
          </DialogHeader>
          {editorFor && (
            <AdminGroupLayoutEditor
              groupId={editorFor.id}
              groupName={editorFor.name}
              getAuthHeaders={getAuthHeaders}
              onClose={() => setEditorFor(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLayoutPresets;
